import { Trans, useLingui } from '@lingui/react/macro'

import { TTS_MAX_TEXT_LENGTH } from '../../_constant'

import VoiceGrid from './VoiceGrid'

import type { VoiceItem } from '../../_types/form'

import EnhanceTextArea from '@/components/EnhanceTextArea'
import InfoPopover from '@/pages/tad.ai/_components/InfoPopover'


import { cls } from 'tagged-classnames-free';
interface DialogueCardProps {
  index: number
  text: string
  voiceId: string
  displayVoices: VoiceItem[]
  canDelete: boolean
  voicesLoading?: boolean
  currentPlayingId?: string | null
  currentSourceId?: string | null
  isPlaying?: boolean
  playLoading?: boolean
  onTextChange: (text: string) => void
  onVoiceSelect: (voice: VoiceItem) => void
  onDelete: () => void
  onViewMore: () => void
  onTogglePlay?: (voice: VoiceItem) => void
}

function DialogueCard(props: DialogueCardProps) {
  const {
    index,
    text,
    voiceId,
    displayVoices,
    canDelete,
    voicesLoading = false,
    currentPlayingId,
    currentSourceId,
    isPlaying = false,
    playLoading = false,
    onTextChange,
    onVoiceSelect,
    onDelete,
    onViewMore,
    onTogglePlay,
  } = props
  const { t } = useLingui()

  return (
    <div className={cls`bg-f-bg-elevated flex flex-col gap-4 rounded-xl`}>
      {index > 0 && <div className={cls`border-f-border border-b`} />}

      {/* Header */}
      <div className={cls`flex items-center justify-between`}>
        <span className={cls`text-f-text text-lg font-semibold`}>
          <Trans>Line {index + 1}</Trans>
        </span>
        {canDelete && (
          <button
            type='button'
            onClick={onDelete}
            className={cls`text-f-text-quaternary hover:text-f-text-secondary rounded bg-[#252533] px-4 py-1 text-sm transition-colors hover:bg-[#2A2A3D]`}
          >
            <Trans>Remove</Trans>
          </button>
        )}
      </div>

      {/* Text */}
      <div className={cls`flex flex-col gap-2`}>
        <div className={cls`flex items-center gap-1`}>
          <span className={cls`text-f-text text-lg font-semibold`}>
            <Trans>Text</Trans>
          </span>
          <InfoPopover
            content={t`All lines in your dialogue share a ${TTS_MAX_TEXT_LENGTH.toLocaleString()} character limit.`}
          />
        </div>
        <EnhanceTextArea
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={t`Enter the text you want to convert to speech`}
          maxLength={TTS_MAX_TEXT_LENGTH}
          count={{
            show: true,
            max: TTS_MAX_TEXT_LENGTH,
          }}
          showClear
          rows={5}
          style={{ resize: 'none' }}
          className={cls`text-sm`}
          containerClassName={cls`
            border-f-border-secondary
            bg-f-bg-elevated
            hover:bg-f-bg-elevated
          `}
        />
      </div>

      {/* Voice */}
      <div className={cls`flex flex-col gap-3`}>
        <div className={cls`flex items-center justify-between`}>
          <span className={cls`text-f-text text-lg font-semibold`}>
            <Trans>Voice</Trans>
          </span>
          {!voicesLoading && (
            <button
              type='button'
              onClick={onViewMore}
              className={cls`text-f-text-quaternary hover:text-f-text-secondary text-sm transition-colors`}
            >
              <Trans>View More</Trans>
            </button>
          )}
        </div>
        <VoiceGrid
          voices={displayVoices}
          selectedVoiceId={voiceId}
          onSelect={onVoiceSelect}
          loading={voicesLoading}
          currentPlayingId={currentPlayingId}
          currentSourceId={currentSourceId}
          sourceId={`grid-${index}`}
          isPlaying={isPlaying}
          playLoading={playLoading}
          onTogglePlay={onTogglePlay}
        />
      </div>
    </div>
  )
}

export default DialogueCard
